import React, { FC } from 'react';
import { createRoot } from 'react-dom/client';
import { QRPreviewWrapper } from '../components/QRPreviewWrapper';
import { AdvancedQRCode } from '../components/AdvancedQRCode';
import { getWatermarkSVG } from '../components/watermarks/getWatermarkSVG';
import { QRData, QRType } from '../types/qr';
import { Frame } from '../types/index';
import { LogoType } from '../components/LogoCustomization';
import { getBackendUrl } from '../utils/constants';

interface DownloadQRCodeParams {
    qrCodeRef: React.RefObject<HTMLDivElement>;
    isDynamicQR: boolean;
    qrType: QRType;
    qrData: QRData;
    generateQRCodeData: (qrType: QRType, qrData: QRData) => Promise<string>;
    cutter: string;
    cutterColor: string;
    opacity: number;
    frame: string | Frame;
    frameColor: string;
    frameThickness: number;
    watermark: string;
    watermarkColor: string;
    watermarkOpacity: number;
    markerShape: string;
    markerColor: string;
    logo: {
        type: LogoType;
        src: string | null;
        width?: number;
        height?: number;
    } | null;
    shape: string;
    size: number;
    hideBackground: boolean;
}

export const downloadQRCode = async ({
    qrCodeRef,
    isDynamicQR,
    qrType,
    qrData,
    generateQRCodeData,
    cutter,
    cutterColor,
    opacity,
    frame,
    frameColor,
    frameThickness,
    watermark,
    watermarkColor,
    watermarkOpacity,
    markerShape,
    markerColor,
    logo,
    shape,
    size,
    hideBackground,
}: DownloadQRCodeParams): Promise<void> => {
    try {
        console.log("Download started");
        
        let finalUrl;
        
        if (isDynamicQR) {
            // Handle dynamic QR code generation
            if (qrType === 'url') {
                const formData = new FormData();
                formData.append('title', qrData.url?.title || '');
                formData.append('description', qrData.url?.description || '');
                formData.append('buttonText', qrData.url?.buttonText || '');
                formData.append('buttonColor', qrData.url?.buttonColor || '');
                formData.append('actionUrl', qrData.url?.actionUrl || '');
                
                if (qrData.url?.bannerImageData) {
                    formData.append('file', qrData.url.bannerImageData);
                }

                const response = await fetch(`${getBackendUrl()}/api/upload/url`, {
                    method: 'POST',
                    body: formData,
                });

                const result = await response.json();
                if (!result.success || !result.qrId) {
                    throw new Error('Failed to create URL QR code');
                }
                
                finalUrl = `${getBackendUrl()}/qr/${result.qrId}`;
            } else {
                // For non-URL types, generate the QR code data using the existing function
                finalUrl = await generateQRCodeData(qrType, qrData);
            }
        } else {
            // Handle static QR code generation
            switch (qrType) {
                case 'url':
                    finalUrl = qrData.url?.actionUrl || '';
                    break;
                case 'email':
                    const { address, subject, message } = qrData.email || {};
                    finalUrl = `mailto:${address || ''}?subject=${encodeURIComponent(subject || '')}&body=${encodeURIComponent(message || '')}`;
                    break;
                // Add other cases for different QR types
                default:
                    finalUrl = '';
            }
        }

        // Update QR code with the correct data before downloading
        const tempDiv = document.createElement('div');
        const root = createRoot(tempDiv);
        const qrProps = {
            data: finalUrl,
            size,
            markerShape,
            markerColor,
            shape,
            qrColor: markerColor,
            logo,
            hideBackground
        };

        const wrapperProps = {
            cutter,
            cutterColor,
            opacity,
            frame,
            frameColor,
            frameThickness,
            watermark,
            watermarkColor,
            watermarkOpacity,
            markerShape,
            markerColor,
            logo,
            shape,
            children: React.createElement(AdvancedQRCode, qrProps)
        };

        const QRComponent: FC<{}> = () => React.createElement(QRPreviewWrapper, wrapperProps);
        root.render(React.createElement(QRComponent));

        // Create a new canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error("Could not get canvas context");
        }

        console.log("Drawing frame:", { frame, frameColor, frameThickness });

        // Set canvas size to 4x original size
        canvas.width = 1100;
        canvas.height = 1100;

        // Draw frame if it exists
        if (frame !== 'none') {
            try {
                let frameSvg = '';
                if (typeof frame === 'string') {
                    frameSvg = `
                        <svg xmlns="http://www.w3.org/2000/svg" width="1100" height="1100">
                            <rect x="0" y="0" width="1100" height="1100" 
                                fill="none" 
                                stroke="${frameColor}" 
                                stroke-width="${frameThickness * 4}"
                                rx="${frame === 'rounded' ? '80' : '0'}"
                            />
                        </svg>
                    `;
                } else if (frame.type === 'colorful' && frame.svg) {
                    frameSvg = frame.svg;
                }

                if (frameSvg) {
                    const frameBlob = new Blob([frameSvg], { type: 'image/svg+xml;charset=utf-8' });
                    const frameUrl = URL.createObjectURL(frameBlob);
                    
                    await new Promise((resolve, reject) => {
                        const frameImg = new Image();
                        frameImg.onload = () => {
                            ctx.drawImage(frameImg, 0, 0, 1100, 1100);
                            URL.revokeObjectURL(frameUrl);
                            resolve(null);
                        };
                        frameImg.onerror = reject;
                        frameImg.src = frameUrl;
                    });
                }
            } catch (frameError) {
                console.error("Frame drawing error:", frameError);
            }
        }

        // Get QR code SVG
        if (!qrCodeRef.current) {
            throw new Error("QR code reference not found");
        }
        
        const qrElement = qrCodeRef.current.querySelector('svg');
        if (!qrElement) {
            throw new Error("QR SVG element not found");
        }

        console.log("Drawing QR code");
        const svgData = new XMLSerializer().serializeToString(qrElement);
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const svgUrl = URL.createObjectURL(svgBlob);

        await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                ctx.drawImage(img, 60, 60, 980, 980);
                URL.revokeObjectURL(svgUrl);
                resolve(null);
            };
            img.onerror = reject;
            img.src = svgUrl;
        });

        // Draw watermark
        if (watermark !== 'none') {
            console.log("Drawing watermark");
            const watermarkSvg = getWatermarkSVG(watermark, watermarkColor);
            const watermarkBlob = new Blob([watermarkSvg], { type: 'image/svg+xml;charset=utf-8' });
            const watermarkUrl = URL.createObjectURL(watermarkBlob);

            await new Promise((resolve, reject) => {
                const watermarkImg = new Image();
                watermarkImg.onload = () => {
                    ctx.globalAlpha = Number(watermarkOpacity);
                    ctx.drawImage(watermarkImg, 60, 60, 980, 980);
                    ctx.globalAlpha = 1.0;
                    URL.revokeObjectURL(watermarkUrl);
                    resolve(null);
                };
                watermarkImg.onerror = reject;
                watermarkImg.src = watermarkUrl;
            });
        }

        console.log("Creating download");
        const dataUrl = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = 'qr-code.png';
        link.href = dataUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        root.unmount();
    } catch (error) {
        console.error("Error generating/downloading QR code:", error);
        console.log("Error details:", {
            message: (error as Error).message,
            stack: (error as Error).stack,
            elements: qrCodeRef.current?.innerHTML
        });
    }
}; 